import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, setBlockTracking as _setBlockTracking, createTextVNode as _createTextVNode, withCtx as _withCtx, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "outer" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "overlay top-left fill-block" }
const _hoisted_4 = {
  key: 0,
  class: "left-btn dir-btn"
}
const _hoisted_5 = {
  key: 1,
  class: "right-btn dir-btn"
}

import { nextTick, computed, onMounted, ref, useTemplateRef } from 'vue';
import { useResizeObserver } from '@vueuse/core';

import NcActions from '@nextcloud/vue/components/NcActions';
import NcActionButton from '@nextcloud/vue/components/NcActionButton';

import { cacheData, getCachedData } from '@services/cache';
import { t } from '@services/l10n';
import { config } from '@services/user-config';
import { useRouteState } from '@services/route-state';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as utils from '@services/utils/common';
import * as dav from '@services/dav';

import LeftMoveIcon from 'vue-material-design-icons/ChevronLeft.vue';
import RightMoveIcon from 'vue-material-design-icons/ChevronRight.vue';
import XImgFade from '@components/frame/XImgFade.vue';

import type { IPhoto } from '@typings';

interface IYear {
  year: number;
  url: string;
  preview: IPhoto;
  photos: IPhoto[];
  text: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OnThisDay',
  emits: ["load"],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit;

const inner = useTemplateRef<HTMLDivElement>('inner');

const years = ref<IYear[] | null>(null);
const hasRight = ref(false);
const hasLeft = ref(false);
const scrollStack = ref<number[]>([]);
const abort = useAbort();

useResizeObserver(inner, onScroll);

useRouteState({ state: { years }, scroll: { inner } });

const isReady = computed(() => years.value !== null);
const isContent = computed(() => !!years.value?.length);

onMounted(() => {
  inner.value!.addEventListener('scroll', onScroll, { passive: true });
  void refresh();
});

async function refresh(): Promise<void> {
  const signal = abort.renew();

  try {
    // Look for cache
    const dayIdToday = utils.dateToDayId(new Date());
    const cacheUrl = `/onthisday/${dayIdToday}`;
    const cache = await getCachedData<IPhoto[]>(cacheUrl, { signal });
    signal.throwIfAborted();
    utils.applyAuids(cache);
    if (cache) {
      years.value = process(cache);
      void onLoad();
    }
    signal.throwIfAborted();

    // Network request
    const photos = await dav.getOnThisDayRaw({ signal });
    signal.throwIfAborted();
    utils.applyAuids(photos);
    cacheData(cacheUrl, photos);

    // Check if exactly same as cache
    if (cache?.length === photos.length && cache.every((p, i) => p.fileid === photos[i].fileid)) return;
    years.value = process(photos);
    void onLoad();
  } catch (e) {
    if (isAbortError(e)) return;
    console.error('Failed to fetch On This Day:', e);
  } finally {
    if (!signal.aborted) {
      years.value ??= [];
    }
  }
}

function process(photos: IPhoto[]): IYear[] {
  const list: IYear[] = [];

  let currentText = '';
  let prevDayId = Number.MAX_SAFE_INTEGER;

  for (const photo of photos) {
    // Skip hidden files
    if (!photo.dayid) continue;
    if (photo.ishidden) continue;
    if (photo.basename?.startsWith('.')) continue;

    photo.key = `${photo.fileid}`;

    // New anniversary, not calendar year (breaks at year boundary).
    // Mirrors Timeline.vue. DateTime calls are expensive.
    if (Math.abs(prevDayId - photo.dayid) > 30) {
      const dateTaken = utils.dayIdToDate(photo.dayid);
      const year = dateTaken.getUTCFullYear();
      const text = utils.getFromNowStr(dateTaken, { padding: 10 });
      if (text !== currentText) {
        list.push({
          year,
          text,
          url: '',
          preview: null!,
          photos: [],
        });
        currentText = text;
      }
    }
    prevDayId = photo.dayid;

    const yearObj = list[list.length - 1];
    yearObj.photos.push(photo);
  }

  // For each year, randomly choose 10 photos to display
  for (const year of list) {
    year.photos = utils.randomSubarray(year.photos, config.onthisday_photos_per_year);
  }

  // Choose preview photo
  for (const year of list) {
    year.preview ||= utils.randomChoice(year.photos);
    year.url = utils.getPreviewUrl({
      photo: year.preview,
      msize: 512,
    });
  }

  return list;
}

async function onLoad(): Promise<void> {
  emit('load');
  await nextTick();
  onScroll();
}

function moveLeft() {
  const innerVal = inner.value!;
  innerVal.scrollBy(-(scrollStack.value.pop() ?? innerVal.clientWidth), 0);
}

function moveRight() {
  const innerVal = inner.value!;
  const innerRect = innerVal.getBoundingClientRect();
  const nextChild = Array.from(innerVal.children)
    .map((c) => c.getBoundingClientRect())
    .find((rect) => rect.right > innerRect.right);

  let scroll = nextChild ? nextChild.left - innerRect.left : innerVal.clientWidth;
  scroll = Math.min(innerVal.scrollWidth - innerVal.scrollLeft - innerVal.clientWidth, scroll);
  scrollStack.value.push(scroll);
  innerVal.scrollBy(scroll, 0);
}

function onScroll() {
  if (!inner.value) return;
  hasLeft.value = inner.value.scrollLeft > 0;
  hasRight.value = inner.value.clientWidth + inner.value.scrollLeft < inner.value.scrollWidth - 20;
}

function click(year: IYear) {
  const allPhotos = years.value?.flatMap((y) => y.photos) ?? [];
  _m.viewer.openStatic(year.preview, allPhotos, 512);
}

__expose({ isReady, isContent, refresh });

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "inner hide-scrollbar",
      ref_key: "inner",
      ref: inner
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(years.value, (year) => {
        return (_openBlock(), _createElementBlock("div", {
          class: "group",
          key: year.text,
          onClick: ($event: any) => (click(year))
        }, [
          _createVNode(XImgFade, {
            class: "fill-block",
            src: year.url,
            duration: "700ms"
          }, null, 8, ["src"]),
          _createElementVNode("div", _hoisted_3, _toDisplayString(year.text), 1)
        ], 8, _hoisted_2))
      }), 128))
    ], 512),
    (hasLeft.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_unref(NcActions), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NcActionButton), {
                "aria-label": _unref(t)('memories', 'Move left'),
                onClick: moveLeft
              }, {
                icon: _withCtx(() => [
                  _cache[0] || (
                    _setBlockTracking(-1, true),
                    (_cache[0] = _createVNode(LeftMoveIcon, { size: 28 })).cacheIndex = 0,
                    _setBlockTracking(1),
                    _cache[0]
                  )
                ]),
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Move left')) + " ", 1)
                ]),
                _: 1
              }, 8, ["aria-label"])
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    (hasRight.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(_unref(NcActions), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NcActionButton), {
                "aria-label": _unref(t)('memories', 'Move right'),
                onClick: moveRight
              }, {
                icon: _withCtx(() => [
                  _cache[1] || (
                    _setBlockTracking(-1, true),
                    (_cache[1] = _createVNode(RightMoveIcon, { size: 28 })).cacheIndex = 1,
                    _setBlockTracking(1),
                    _cache[1]
                  )
                ]),
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Move right')) + " ", 1)
                ]),
                _: 1
              }, 8, ["aria-label"])
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true)
  ], 512)), [
    [_vShow, isReady.value && isContent.value]
  ])
}
}

})